import { Card, Chip, Container, Stack, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect } from "react";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldDriverLogGroup } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import Label from "src/components/label";
import { formatDatetimeForPreview, formatTimeAsHourMin } from "src/utils/date-utils";
import { Violations } from "src/components/violations";
import { EldDriverStatusLabel } from "src/components/eld-driver-status-label";
import { useAuthContext } from "src/auth/hooks";

type EldDriverLogGroupsProps = {
  eldDriverLogGroups?: EldDriverLogGroup[],
  onEldDriverLogGroupSelected: (a: EldDriverLogGroup) => void
}

const EldDriverLogGroups = memo(({ eldDriverLogGroups, onEldDriverLogGroupSelected }: EldDriverLogGroupsProps) => {
  const { sortedList, sortData } = useSort(eldDriverLogGroups, "firstName");

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["firstName", "lastName", "username", "lastStatus", "lastLocation", "vehicleUnitNumber", "breakTime", "driveTime", "shiftTime", "cycleTime", "violations", "eldSerialNumber", "lastConnected", "lastConnectionStatus", "eldSn", "eldVehicleId"]);

  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Driver Logs" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="firstName" sortData={sortData} >
                Driver
              </SortableHeader>

              <SortableHeader sortKey="lastStatus" sortData={sortData} align="center">
                Status
              </SortableHeader>

              <SortableHeader sortKey="lastLocation" sortData={sortData} >
                Location
              </SortableHeader>

              <SortableHeader sortKey="vehicleUnitNumber" sortData={sortData} >
                Vehicle&nbsp;#
              </SortableHeader>

              <SortableHeader sortKey="breakTime" align="right" sortData={sortData} >
                Break
              </SortableHeader>

              <SortableHeader sortKey="driveTime" align="right" sortData={sortData}>
                Drive
              </SortableHeader>

              <SortableHeader sortKey="shiftTime" align="right" sortData={sortData}>
                Shift
              </SortableHeader>

              <SortableHeader sortKey="cycleTime" align="right" sortData={sortData}>
                Cycle
              </SortableHeader>

              <SortableHeader sortKey="violations" sortData={sortData}>
                Violations
              </SortableHeader>

              <SortableHeader sortKey="lastConnectionStatus" sortData={sortData} align="right">
                Eld Status
              </SortableHeader>

              <SortableHeader sortKey="lastConnected" sortData={sortData} align="right">
                Last Connected
              </SortableHeader>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((eldDriverLogGroup, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onEldDriverLogGroupSelected(eldDriverLogGroup)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell>
                {eldDriverLogGroup.firstName} {eldDriverLogGroup.lastName}
                {eldDriverLogGroup.suspended && <Label sx={{ ml: 1, color: "white", background: "#29b6f6" }}>Suspended</Label>}
              </TruxTableCell>

              <TruxTableCell align="center">
                <EldDriverStatusLabel status={eldDriverLogGroup.lastStatus} />
              </TruxTableCell>

              <TruxTableCell>
                {eldDriverLogGroup.lastLocation}
              </TruxTableCell>

              <TruxTableCell>
                {eldDriverLogGroup.vehicleUnitNumber}
              </TruxTableCell>

              <TruxTableCell align="right">
                {formatTimeAsHourMin(eldDriverLogGroup.breakTime)}
              </TruxTableCell>

              <TruxTableCell align="right">
                {formatTimeAsHourMin(eldDriverLogGroup.driveTime)}
              </TruxTableCell>

              <TruxTableCell align="right">
                {formatTimeAsHourMin(eldDriverLogGroup.shiftTime)}
              </TruxTableCell>

              <TruxTableCell align="right">
                {formatTimeAsHourMin(eldDriverLogGroup.cycleTime)}
              </TruxTableCell>

              <TruxTableCell>
                <Violations violations={eldDriverLogGroup.violations} />
                {eldDriverLogGroup.violations.length === 0 && <Warnings eldDriverLogGroup={eldDriverLogGroup} />}
              </TruxTableCell>

              <TruxTableCell align="right">
                {eldDriverLogGroup.lastConnectionStatus &&
                  <Chip
                    label={eldDriverLogGroup.lastConnectionStatus}
                    color={eldDriverLogGroup.lastConnectionStatus === "Disconnected" ? "warning" : "success"}
                    variant="outlined"
                    size="small"
                    clickable={false}
                  />
                }
              </TruxTableCell>
              <TruxTableCell align="right">
                {formatDatetimeForPreview(eldDriverLogGroup.lastConnected, timezone)}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

export function Warnings({ eldDriverLogGroup }: { eldDriverLogGroup: EldDriverLogGroup }) {

  const { session } = useAuthContext();
  const organizationId = session?.organization.id;

  const warnings = [] as string[];

  // ufc organization id
  if (organizationId === 22){
    if (eldDriverLogGroup.driveTime >= 0.25 && eldDriverLogGroup.driveTime < 0.5 && eldDriverLogGroup.lastStatus === "Driving") {
      warnings.push("11h | Driving < 30min")
    }
  
    if (eldDriverLogGroup.breakTime >= 0.25 && eldDriverLogGroup.breakTime < 0.5 && eldDriverLogGroup.lastStatus === "Driving") {
      warnings.push("30m | Break < 30min")
    }
  
    if (eldDriverLogGroup.shiftTime >= 0.25 && eldDriverLogGroup.shiftTime < 0.5 && (eldDriverLogGroup.lastStatus === "Driving" || eldDriverLogGroup.lastStatus === "On Duty")) {
      warnings.push("14h | Shift < 30min")
    }
  
    if (eldDriverLogGroup.cycleTime >= 0.25 && eldDriverLogGroup.cycleTime < 0.5 && (eldDriverLogGroup.lastStatus === "Driving" || eldDriverLogGroup.lastStatus === "On Duty")) {
      warnings.push("70/8 | Cycle < 30min")
    }
  }

  if (eldDriverLogGroup.driveTime < 0.25 && eldDriverLogGroup.driveTime > 0 && eldDriverLogGroup.lastStatus === "Driving") {
    warnings.push("11h | Driving < 15min")
  }

  if (eldDriverLogGroup.breakTime < 0.25 && eldDriverLogGroup.breakTime > 0 && eldDriverLogGroup.lastStatus === "Driving") {
    warnings.push("30m | Break < 15min")
  }

  if (eldDriverLogGroup.shiftTime < 0.25 && eldDriverLogGroup.shiftTime > 0 && (eldDriverLogGroup.lastStatus === "Driving" || eldDriverLogGroup.lastStatus === "On Duty")) {
    warnings.push("14h | Shift < 15min")
  }

  if (eldDriverLogGroup.cycleTime < 0.25 && eldDriverLogGroup.cycleTime > 0 && (eldDriverLogGroup.lastStatus === "Driving" || eldDriverLogGroup.lastStatus === "On Duty")) {
    warnings.push("70/8 | Cycle < 15min")
  }


  return warnings.length > 0 && <Stack direction="row" gap={1} flexWrap="wrap">
    {warnings.map((v, i2) => <Warning key={i2} warning={v} />)}
  </Stack>
}

function Warning({ warning }: { warning: string }) {
  return <Label sx={{color: "white", background: "orange"}}>{warning}</Label>
}
type PageModel = {
  eldDriverLogGroups?: EldDriverLogGroup[],
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    eldDriverLogGroups: axios.get(endpoints.eldDriverLogGroups.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);


  const onEldDriverLogGroupSelected = useCallback((a: EldDriverLogGroup) => {
    router.push(`/app/eld/eld-driver-logs/driver/${a.eldDriverId}`)
  }, [router]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.eldDriverLogGroups?.length !== 0 &&
          <PageHeader title="Driver Logs" />
        }

        {data && data?.eldDriverLogGroups?.length === 0 &&
          <NoneFound title="No Driver Logs Found" message="Please make sure you have drivers added in your account." />
        }

        {data && data?.eldDriverLogGroups?.length !== 0 && <EldDriverLogGroups eldDriverLogGroups={data.eldDriverLogGroups} onEldDriverLogGroupSelected={onEldDriverLogGroupSelected} />}
      </DisplayPageData>
    </Container >
  );
}
